/**
 * Pagina WhatsApi - API Keys (TASK-2224 + TASK-2225 + TASK-2230).
 *
 * Escopo pela organizacao ativa (spec 2026-11-02 §5.1): organizacao -> ChavesDaOrganizacao
 * (onde se cria chave); tenant -> chaves legadas do EC2, sem criar (§5.4: o WhatsApi antigo
 * recusa com unknown_gateway). Apos rotacionar, exibe KeyReveal com a key uma unica vez.
 *
 * Acoes (TASK-2225):
 * - Switch: ativa direto quando inativa; desativa via modal quando ativa. (whatsapi:update)
 * - Rotate: modal de aviso -> nova key via KeyReveal. (whatsapi:update)
 * - Delete: modal com TextInput que exige digitar o nome exato da key. (whatsapi:delete)
 */

import { useState } from 'react'
import {
  ActionIcon, Alert, Anchor, Badge, Button, Center, Code, Group, Modal, Skeleton,
  Stack, Switch, Table, Text, TextInput, Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconAlertCircle, IconKey, IconRefresh, IconTrash, IconPlugOff, IconBook,
  IconBuildingOff,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { raizDoTenant, whatsapiApi } from '@/api/whatsapi'
import type { WaApiKey, WaApiKeyWithSecret } from '@/api/whatsapi'
import { useCan } from '@/store/auth'
import { KeyReveal } from '@/components/whatsapi/KeyReveal'
import { KbDrawer } from '@/components/whatsapi/KbDrawer'
import { ChavesDaOrganizacao } from '@/components/whatsapi/ChavesDaOrganizacao'
import { AvisoLegadoEc2 } from '@/components/whatsapi/AvisoLegadoEc2'
import { SeletorEscopo } from '@/components/whatsapi/SeletorEscopo'
import { TituloDoEscopo } from '@/components/whatsapi/TituloDoEscopo'
import { EmptyState } from '@/components/ui'
import { exigirTenant } from './exigirTenant'
import { useEscopoWhatsApi } from '@/hooks/useEscopoWhatsApi'

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('pt-BR', {
    day:    '2-digit',
    month:  '2-digit',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
  })
}

function formatNullableDateTime(iso: string | null): string {
  if (!iso) return '—'
  return formatDateTime(iso)
}

interface ApiError {
  response?: { status?: number; data?: { detail?: unknown } }
}

/** Codigos do WhatsApi legado -> frase para a tela (spec 2026-11-02 §5.4). */
const DETALHES_LEGADO: Record<string, string> = {
  unknown_gateway: 'Este WhatsApi antigo não envia mais por este gateway. Use as chaves da organização.',
}

/** Mensagem do servidor para o toast. O 422 do FastAPI traz `detail` como LISTA
 *  de `{msg}`; antes só a string era lida e o 422 virava o fallback genérico. */
export function resolveErrorMessage(err: unknown, fallback: string): string {
  const status = (err as ApiError)?.response?.status
  if (status === 409) return 'Nome já existe'
  if (status === 503) return 'Serviço indisponível'
  const detail = (err as ApiError)?.response?.data?.detail
  if (typeof detail === 'string' && detail) return DETALHES_LEGADO[detail] ?? detail
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (d && typeof d === 'object' ? (d as { msg?: unknown }).msg : null))
      .filter((m): m is string => typeof m === 'string' && m.length > 0)
    if (msgs.length) return `${fallback}: ${msgs.join('; ')}`
  }
  return fallback
}


export function WhatsApiApiKeys() {
  const { escopo, opcoes, escolher, nome, carregando: carregandoEscopo } = useEscopoWhatsApi()
  const tenantId = !carregandoEscopo && escopo.tipo === 'tenant' ? escopo.tenantId : undefined
  const temOrganizacao = opcoes.organizacoes.length > 0
  const can = useCan()
  const queryClient = useQueryClient()

  // `create` do recurso whatsapi vale para o KB; chave legada nova nao se cria mais.
  const canCreate = can('whatsapi', 'create')
  const canUpdate = can('whatsapi', 'update')
  const canDelete = can('whatsapi', 'delete')
  const canRead   = can('whatsapi', 'read')
  const canAct    = canUpdate || canDelete || canRead

  const [revealKey, setRevealKey] = useState<string | null>(null)

  const [confirmToggle, setConfirmToggle] = useState<WaApiKey | null>(null)
  const [confirmRotate, setConfirmRotate] = useState<WaApiKey | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<WaApiKey | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [kbKey, setKbKey] = useState<WaApiKey | null>(null)

  const closeDelete = () => {
    setConfirmDelete(null)
    setDeleteConfirmText('')
  }

  const configQuery = useQuery<{ configured: boolean }>({
    queryKey: ['whatsapi-config', tenantId],
    queryFn: () => whatsapiApi.getConfig(tenantId as number),
    enabled: !!tenantId,
    retry: false,
  })
  const notConfigured = configQuery.data?.configured === false

  const { data: keys = [], isLoading, error } = useQuery<WaApiKey[], Error>({
    queryKey: ['whatsapi-keys', tenantId],
    queryFn:  () => whatsapiApi.listApiKeys(tenantId as number),
    enabled:  !!tenantId && !notConfigured,
    retry:    false,
  })

  const toggleMutation = useMutation<WaApiKey, unknown, { key: WaApiKey; next: boolean }>({
    mutationFn: ({ key, next }) =>
      whatsapiApi.updateApiKey(exigirTenant(tenantId), key.id, next),
    onSuccess: () => {
      setConfirmToggle(null)
      queryClient.invalidateQueries({ queryKey: ['whatsapi-keys'] })
    },
    onError: (err) => {
      notifications.show({
        color: 'red', message: resolveErrorMessage(err, 'Falha ao atualizar API key'),
      })
    },
  })

  const rotateMutation = useMutation<WaApiKeyWithSecret, unknown, string>({
    mutationFn: (id: string) => whatsapiApi.rotateApiKey(exigirTenant(tenantId), id),
    onSuccess: (data) => {
      setConfirmRotate(null)
      setRevealKey(data.key)
      queryClient.invalidateQueries({ queryKey: ['whatsapi-keys'] })
    },
    onError: (err) => {
      notifications.show({
        color: 'red', message: resolveErrorMessage(err, 'Falha ao rotacionar API key'),
      })
    },
  })

  const deleteMutation = useMutation<void, unknown, string>({
    mutationFn: (id: string) => whatsapiApi.deleteApiKey(exigirTenant(tenantId), id),
    onSuccess: () => {
      closeDelete()
      queryClient.invalidateQueries({ queryKey: ['whatsapi-keys'] })
    },
    onError: (err) => {
      notifications.show({
        color: 'red', message: resolveErrorMessage(err, 'Falha ao deletar API key'),
      })
    },
  })

  const handleToggle = (key: WaApiKey) => {
    if (key.is_active) {
      setConfirmToggle(key)
    } else {
      toggleMutation.mutate({ key, next: true })
    }
  }

  const cabecalho = (
    <Group justify="space-between" align="center">
      <Group gap="md" align="center">
        <TituloDoEscopo titulo="API Keys" escopo={escopo} nome={nome} seloTenant="Tenant (legado)" order={2} />
        <SeletorEscopo escopo={escopo} opcoes={opcoes} escolher={escolher} />
      </Group>
    </Group>
  )

  if (carregandoEscopo) {
    return (
      <Stack gap="xs">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height={32} radius="sm" />
        ))}
      </Stack>
    )
  }

  if (escopo.tipo === 'organizacao') {
    return (
      <Stack gap="md">
        {cabecalho}
        <ChavesDaOrganizacao orgId={escopo.orgId} pode={escopo.pode} />
      </Stack>
    )
  }

  if (tenantId == null) {
    return (
      <Stack gap="md">
        <EmptyState
          icon={IconBuildingOff}
          accent="warning"
          title="Nenhum tenant disponível"
          description="A API key pertence a um tenant, e a sua organização ainda não tem tenant vinculado. Peça a um administrador para vincular um tenant à organização e habilitar o WhatsApi nele."
        />
      </Stack>
    )
  }

  if (notConfigured) {
    return (
      <Stack gap="md">
        {cabecalho}
        {/* O titulo vem do layout desde 2026-08-13 (routeHeader + PageHeader). Estava aqui
          tambem, repetindo o nome da rota. */}
        <EmptyState
          icon={IconPlugOff}
          accent="warning"
          title="WhatsApi não está habilitado"
          description="Este tenant ainda não tem WhatsApi habilitado. Peça a um administrador para ativar a feature em Administração → Tenants."
          primaryAction={{
            label: 'Ir para Tenants',
            href: '/admin/tenants',
            permission: { resource: 'usuarios', action: 'manage' },
          }}
        />
      </Stack>
    )
  }

  return (
    <Stack gap="md">
      {cabecalho}

      <AvisoLegadoEc2 />

      {revealKey && (
        <KeyReveal keyValue={revealKey} onClose={() => setRevealKey(null)} />
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Não foi possível carregar as API keys. Tente novamente em instantes.
        </Alert>
      )}

      {isLoading ? (
        <Stack gap="xs">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={32} radius="sm" />
          ))}
        </Stack>
      ) : keys.length === 0 ? (
        <Center py="xl">
          <Stack gap="xs" align="center">
            <IconKey size={40} color="var(--mantine-color-gray-5)" />
            <Text c="dimmed" size="sm">
              Nenhuma chave legada neste tenant. Chave nova se cria no gateway da organização.
            </Text>
            {temOrganizacao ? (
              // Sem orgId: o hook resolve a organização ativa do menu antes da primeira da lista.
              <Button variant="light" size="xs" onClick={() => escolher('organizacao')}>
                Ver as chaves da organização
              </Button>
            ) : (
              <Anchor size="sm" href="/admin/whatsapp-gateways">
                Ir para Administração → Gateways WhatsApp
              </Anchor>
            )}
          </Stack>
        </Center>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nome</Table.Th>
              <Table.Th>Prefixo</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Criada em</Table.Th>
              <Table.Th>Último uso</Table.Th>
              {canAct && <Table.Th>Ações</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {keys.map((k) => (
              <Table.Tr key={k.id}>
                <Table.Td>
                  <Text size="sm">{k.name}</Text>
                </Table.Td>
                <Table.Td>
                  <Code>{k.key_prefix}...</Code>
                </Table.Td>
                <Table.Td>
                  <Badge color={k.is_active ? 'teal' : 'gray'} variant="light">
                    {k.is_active ? 'Ativa' : 'Inativa'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">{formatDateTime(k.created_at)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">{formatNullableDateTime(k.last_used_at)}</Text>
                </Table.Td>
                {canAct && (
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      {canUpdate && (
                        <Tooltip label={k.is_active ? 'Desativar' : 'Ativar'}>
                          <Switch
                            aria-label={`${k.is_active ? 'Desativar' : 'Ativar'} ${k.name}`}
                            checked={k.is_active}
                            onChange={() => handleToggle(k)}
                          />
                        </Tooltip>
                      )}
                      {canUpdate && (
                        <Tooltip label="Rotacionar">
                          <ActionIcon
                            aria-label={`Rotacionar ${k.name}`}
                            variant="subtle"
                            color="blue"
                            onClick={() => setConfirmRotate(k)}
                          >
                            <IconRefresh size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip label="Deletar">
                          <ActionIcon
                            aria-label={`Deletar ${k.name}`}
                            variant="subtle"
                            color="red"
                            onClick={() => setConfirmDelete(k)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {canRead && (
                        <Tooltip label="Base de Conhecimento">
                          <ActionIcon
                            aria-label={`Base de Conhecimento de ${k.name}`}
                            variant="subtle"
                            color="grape"
                            onClick={() => setKbKey(k)}
                          >
                            <IconBook size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal
        opened={!!confirmToggle}
        onClose={() => setConfirmToggle(null)}
        title="Desativar API Key"
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Desativar a key <Code>{confirmToggle?.name}</Code>? Requisições que a
            utilizarem passarao a responder <Code>401 Unauthorized</Code>
            imediatamente.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setConfirmToggle(null)}>
              Cancelar
            </Button>
            <Button
              color="red"
              loading={toggleMutation.isPending}
              onClick={() => {
                if (confirmToggle) {
                  toggleMutation.mutate({ key: confirmToggle, next: false })
                }
              }}
            >
              Desativar
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!confirmRotate}
        onClose={() => setConfirmRotate(null)}
        title="Rotacionar API Key"
        size="sm"
      >
        <Stack gap="md">
          <Alert color="orange" icon={<IconAlertCircle size={16} />}>
            A key atual sera invalidada imediatamente. Qualquer integração que
            ainda usar a chave antiga passara a receber <Code>401</Code>.
          </Alert>
          <Text size="sm">
            Confirma rotacionar <Code>{confirmRotate?.name}</Code>?
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setConfirmRotate(null)}>
              Cancelar
            </Button>
            <Button
              color="orange"
              loading={rotateMutation.isPending}
              onClick={() => {
                if (confirmRotate) rotateMutation.mutate(confirmRotate.id)
              }}
            >
              Rotacionar
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!confirmDelete}
        onClose={closeDelete}
        title="Deletar API Key"
        size="sm"
      >
        <Stack gap="md">
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            Esta ação não pode ser desfeita. A key sera removida permanentemente.
          </Alert>
          <Text size="sm">
            Para confirmar, digite o nome exato da key:{' '}
            <Code>{confirmDelete?.name}</Code>
          </Text>
          <TextInput
            aria-label="Confirmar nome da API key"
            placeholder={confirmDelete?.name ?? ''}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.currentTarget.value)}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={closeDelete}>
              Cancelar
            </Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              disabled={deleteConfirmText !== confirmDelete?.name}
              onClick={() => {
                if (confirmDelete) deleteMutation.mutate(confirmDelete.id)
              }}
            >
              Deletar
            </Button>
          </Group>
        </Stack>
      </Modal>

      {tenantId != null && (
        <KbDrawer
          raiz={raizDoTenant(tenantId)}
          apiKey={kbKey}
          pode={{ criar: canCreate, atualizar: canUpdate, apagar: canDelete }}
          opened={!!kbKey}
          onClose={() => setKbKey(null)}
        />
      )}
    </Stack>
  )
}
